/**
 * Adapter for the layout engine system
 * Provides a unified interface for different layout strategies
 */
import { logger } from '@/utils/logger';

import EdgeRouter from './edge-routing';
import HierarchicalLayoutEngine from './hierarchical-layout-engine';

/**
 * Layout engine adapter that provides access to multiple layout strategies
 */
class LayoutEngine {
  /**
   * Apply a layered layout that prevents edge crossings
   * @param {Array} nodes - The react-flow nodes to arrange
   * @param {Array} edges - The react-flow edges connecting nodes
   * @returns {Array} The arranged nodes
   */
  static applyLayeredLayout(nodes, edges, options) {
    // Use hierarchical layout as the improved layered layout
    return this.applyHierarchicalLayout(nodes, edges, {
      edgeBundling: false, // Disable edge bundling for cleaner appearance
      ...options,
    });
  }

  /**
   * Apply a hierarchical layout optimized for flow diagrams
   * @param {Array} nodes - The react-flow nodes to arrange
   * @param {Array} edges - The react-flow edges connecting nodes
   * @param {Object} options - Layout options
   * @returns {Array} The arranged nodes
   */
  static applyHierarchicalLayout(nodes, edges, options = {}) {
    try {
      const defaultOptions = {
        direction: 'LR',
        edgeRouting: 'default',
        edgeBundling: true,
        considerNodeSizes: true,
        respectActionFrames: true, // Changed from false to true by default
      };

      const layoutOptions = { ...defaultOptions, ...options };

      // Separate nodes into parent frames, regular nodes, and child nodes
      const parentFrames = nodes.filter((node) => node.type === 'actionFrame');
      const childNodes = nodes.filter((node) => node.parentNode);
      const regularNodes = nodes.filter((node) => !node.parentNode && node.type !== 'actionFrame');

      // If we have parent frames and child nodes, we need special handling
      if (parentFrames.length > 0 && layoutOptions.respectActionFrames) {
        // Create a map of node id to parent frame id
        const nodeToFrameMap = {};
        childNodes.forEach((node) => {
          nodeToFrameMap[node.id] = node.parentNode;
        });

        // Create virtual edges for connections between frames
        const virtualEdges = [];

        // Create virtual edges between parent frames based on child connections
        edges.forEach((edge) => {
          const sourceFrame = nodeToFrameMap[edge.source];
          const targetFrame = nodeToFrameMap[edge.target];

          // If both nodes are in different parent frames, create a virtual edge
          if (sourceFrame && targetFrame && sourceFrame !== targetFrame) {
            // Check if this virtual edge already exists
            const existingEdge = virtualEdges.find(
              (e) => e.source === sourceFrame && e.target === targetFrame
            );

            if (!existingEdge) {
              virtualEdges.push({
                id: `virtual-${sourceFrame}-${targetFrame}`,
                source: sourceFrame,
                target: targetFrame,
              });
            }
          }
        });

        // Include edges between regular nodes and parent frames
        edges.forEach((edge) => {
          // Check if source is regular node and target is in a frame
          const targetFrame = nodeToFrameMap[edge.target];
          if (!nodeToFrameMap[edge.source] && targetFrame) {
            virtualEdges.push({
              id: `virtual-reg-${edge.source}-${targetFrame}`,
              source: edge.source,
              target: targetFrame,
            });
          }

          // Check if target is regular node and source is in a frame
          const sourceFrame = nodeToFrameMap[edge.source];
          if (!nodeToFrameMap[edge.target] && sourceFrame) {
            virtualEdges.push({
              id: `virtual-reg-${sourceFrame}-${edge.target}`,
              source: sourceFrame,
              target: edge.target,
            });
          }
        });

        // First phase: Apply layout to parent frames and regular nodes
        const mainNodes = [...regularNodes, ...parentFrames];

        // Set initial parent sizes
        parentFrames.forEach((frame) => {
          const frameChildren = childNodes.filter((node) => node.parentNode === frame.id);
          // Set a reasonable initial size based on child count
          const width = Math.max(180, frameChildren.length * 150);
          const height = Math.max(120, frameChildren.length * 80);
          frame.style = { ...frame.style, width, height };
        });

        // Apply hierarchical layout to main nodes and virtual edges
        const result = HierarchicalLayoutEngine.applyLayout(
          mainNodes,
          [...edges, ...virtualEdges],
          layoutOptions
        );

        const layoutedMainNodes = result.nodes;

        // Second phase: Position child nodes within their parents
        const resultNodes = [...layoutedMainNodes];

        // For each parent frame, layout its children
        parentFrames.forEach((parentFrame) => {
          // Find the positioned parent frame
          const positionedParent = layoutedMainNodes.find((n) => n.id === parentFrame.id);
          if (!positionedParent) return;

          // Get all children for this frame
          const frameChildren = childNodes.filter((node) => node.parentNode === parentFrame.id);
          if (frameChildren.length === 0) return;

          // Create internal edges (edges between children of this frame)
          const internalEdges = edges.filter((edge) => {
            const sourceIsChild = frameChildren.some((n) => n.id === edge.source);
            const targetIsChild = frameChildren.some((n) => n.id === edge.target);
            return sourceIsChild && targetIsChild;
          });

          // Apply mini hierarchical layout to children
          const childOptions = {
            ...layoutOptions,
            layerDistance: layoutOptions.layerDistance / 2,
            nodeDistance: layoutOptions.nodeDistance / 2,
          };

          // Apply hierarchical layout to children
          const childResult = HierarchicalLayoutEngine.applyLayout(
            frameChildren,
            internalEdges,
            childOptions
          );
          const layoutedChildren = childResult.nodes;

          // Adjust children positions to be relative to parent
          const offsetX = 40; // Left padding inside parent
          const offsetY = 50; // Top padding inside parent (for header)

          const adjustedChildren = layoutedChildren.map((child) => ({
            ...child,
            position: {
              x: offsetX + child.position.x,
              y: offsetY + child.position.y,
            },
            parentNode: positionedParent.id,
            extent: 'parent',
          }));

          // Calculate required parent size based on children layout
          let maxChildRight = 0;
          let maxChildBottom = 0;

          adjustedChildren.forEach((child) => {
            // For calculating frames, we need to know child dimensions even if we don't explicitly set them
            const childWidth = child.style?.width || child.width || 150;
            const childHeight = child.style?.height || child.height || 60;
            const right = child.position.x + childWidth;
            const bottom = child.position.y + childHeight;
            maxChildRight = Math.max(maxChildRight, right);
            maxChildBottom = Math.max(maxChildBottom, bottom);
          });

          // Add padding to parent size
          const padding = 60;
          const requiredWidth = maxChildRight + padding;
          const requiredHeight = maxChildBottom + padding;

          // Update parent with adjusted size
          const adjustedParent = {
            ...positionedParent,
            style: {
              ...positionedParent.style,
              width: Math.max(positionedParent.style?.width || 180, requiredWidth),
              height: Math.max(positionedParent.style?.height || 120, requiredHeight),
            },
          };

          // Update parent in result
          const parentIndex = resultNodes.findIndex((n) => n.id === parentFrame.id);
          if (parentIndex !== -1) {
            resultNodes[parentIndex] = adjustedParent;
          } else {
            resultNodes.push(adjustedParent);
          }

          // Add adjusted children to result
          resultNodes.push(...adjustedChildren);
        });

        return resultNodes;
      }

      // For standard hierarchical layout with no action frames, use the normal flow
      const result = HierarchicalLayoutEngine.applyLayout(nodes, edges, layoutOptions);
      return result.nodes;
    } catch (error) {
      logger.error('Layout engine error:', error);

      // Fallback: return nodes with basic positioning
      return nodes.map((node, index) => ({
        ...node,
        position: {
          x: (index % 3) * 300, // Simple grid layout as fallback
          y: Math.floor(index / 3) * 200,
        },
      }));
    }
  }

  /**
   * Route edges around action frames for better visual representation
   * @param {Array} edges - The original edges
   * @param {Array} nodes - All nodes with positions
   * @param {Object} actionFrames - Action frame metadata
   * @returns {Array} Enhanced edges with better routing
   */
  static routeEdgesWithActionFrames(edges, nodes, actionFrames) {
    if (!edges || !nodes || edges.length === 0 || nodes.length === 0) {
      return edges;
    }

    // Create node lookup map and calculate node dimensions
    const nodeMap = new Map();
    nodes.forEach((node) => {
      // Fallback logic: prefer style properties (actively managed by layout engine)
      // but fall back to root-level properties for robustness if style properties are missing
      const width = node.style?.width || node.width || 250;
      const height = node.style?.height || node.height || 100;

      nodeMap.set(node.id, {
        ...node,
        width,
        height,
        // Calculate center points for edge routing
        centerX: node.position.x + width / 2,
        centerY: node.position.y + height / 2,
        // Calculate bounds for collision detection
        left: node.position.x,
        top: node.position.y,
        right: node.position.x + width,
        bottom: node.position.y + height,
      });
    });

    // Create map of node to parent frame
    const nodeToFrameMap = new Map();
    nodes.forEach((node) => {
      if (node.parentNode) {
        nodeToFrameMap.set(node.id, node.parentNode);
      }
    });

    // Get parent frame nodes with their bounds
    const frameNodes = new Map();
    Object.keys(actionFrames).forEach((frameId) => {
      const frameNode = nodeMap.get(frameId);
      if (frameNode) {
        frameNodes.set(frameId, frameNode);
      }
    });

    // For complex edge routing scenarios, we can use the EdgeRouter
    const complexRoutingEnabled = edges.length > 20;
    if (complexRoutingEnabled) {
      // Use EdgeRouter for complex routing requirements

      // First create straight edge paths, then optimize with EdgeRouter
      const preparedEdges = EdgeRouter.prepareEdges(edges, nodeMap);
      return preparedEdges;
    }

    const enhancedEdges = [];

    // Process each edge
    edges.forEach((edge) => {
      const sourceNodeData = nodeMap.get(edge.source);
      const targetNodeData = nodeMap.get(edge.target);

      if (!sourceNodeData || !targetNodeData) {
        // If source or target node not found, just pass through the edge
        enhancedEdges.push(edge);
        return;
      }

      // Get parent frames for source and target
      const sourceFrame = nodeToFrameMap.get(edge.source);
      const targetFrame = nodeToFrameMap.get(edge.target);

      // Base enhanced edge with improved styling
      const enhancedEdge = {
        ...edge,
        animated: edge.animated,
        style: {
          ...edge.style,
          strokeWidth: edge.style?.strokeWidth || 2,
        },
        zIndex: 10, // High z-index for visibility
      };

      // Set marker end for all edges
      enhancedEdge.markerEnd = {
        type: 'arrowclosed',
        width: 10,
        height: 10,
        color: enhancedEdge.style?.stroke || '#555',
      };

      // Case 1: Both nodes in same frame - simple smooth edge
      if (sourceFrame && targetFrame && sourceFrame === targetFrame) {
        enhancedEdge.type = 'default';
        enhancedEdges.push(enhancedEdge);
        return;
      }

      // Case 2: Cross-frame or frame-to-regular edge - use smoothstep routing instead of orthogonal
      enhancedEdge.type = 'default';

      // Add routing data
      enhancedEdge.data = {
        ...enhancedEdge.data,
        // Add visual distinction based on edge type
        strokeWidth: sourceFrame && targetFrame ? 2 : 1.5,
      };

      enhancedEdges.push(enhancedEdge);
    });

    return enhancedEdges;
  }
}

export default LayoutEngine;
